import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const IntegrationTab = () => {
  const [integrations, setIntegrations] = useState([
    {
      id: 1,
      name: "SAP Business One",
      type: "ERP",
      status: "connected",
      lastSync: new Date(Date.now() - 1800000),
      config: {
        serverUrl: "https://sap.company.local:50000",
        database: "COMPANY_DB",
        username: "audit_user",
        syncInterval: "15",
        autoSync: true
      }
    },
    {
      id: 2,
      name: "QuickBooks Online",
      type: "Accounting",
      status: "disconnected",
      lastSync: null,
      config: {
        clientId: "",
        redirectUri: "https://auditai.sa/callback/quickbooks",
        syncInterval: "30",
        autoSync: false
      }
    },
    {
      id: 3,
      name: "Microsoft Dynamics 365",
      type: "ERP",
      status: "error",
      lastSync: new Date(Date.now() - 86400000),
      config: {
        tenantId: "company-tenant-id",
        clientId: "dynamics-client-id",
        syncInterval: "60",
        autoSync: true
      }
    }
  ]);

  const [syncSettings, setSyncSettings] = useState({
    globalAutoSync: true,
    syncFrequency: "30",
    conflictResolution: "manual",
    dataMapping: {
      customers: true,
      products: true,
      invoices: true,
      payments: true
    }
  });

  const [isLoading, setIsLoading] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState(null);

  const handleIntegrationUpdate = (id, field, value) => {
    setIntegrations(prev =>
      prev?.map(integration =>
        integration?.id === id
          ? { ...integration, config: { ...integration?.config, [field]: value } }
          : integration
      )
    );
  };

  const handleSyncSettingChange = (field, value) => {
    setSyncSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDataMappingChange = (field, value) => {
    setSyncSettings(prev => ({
      ...prev,
      dataMapping: {
        ...prev?.dataMapping,
        [field]: value
      }
    }));
  };

  const connectIntegration = async (id) => {
    setIsLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 2000));
      setIntegrations(prev =>
        prev?.map(integration =>
          integration?.id === id
            ? { ...integration, status: 'connected', lastSync: new Date() }
            : integration
        )
      );
    } catch (error) {
      console.error('Connection failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const disconnectIntegration = (id) => {
    setIntegrations(prev =>
      prev?.map(integration =>
        integration?.id === id
          ? { ...integration, status: 'disconnected', lastSync: null }
          : integration
      )
    );
  };

  const syncNow = async (id) => {
    setIsLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      setIntegrations(prev =>
        prev?.map(integration =>
          integration?.id === id
            ? { ...integration, lastSync: new Date() }
            : integration
        )
      );
    } catch (error) {
      console.error('Sync failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'connected':
        return <Icon name="CheckCircle" size={20} className="text-success" />;
      case 'error':
        return <Icon name="XCircle" size={20} className="text-error" />;
      default:
        return <Icon name="AlertCircle" size={20} className="text-muted-foreground" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'connected':
        return 'text-success';
      case 'error':
        return 'text-error';
      default:
        return 'text-muted-foreground';
    }
  };

  const formatLastSync = (date) => {
    if (!date) return 'Never';
    const now = new Date();
    const diffMinutes = Math.floor((now - date) / (1000 * 60));
    
    if (diffMinutes < 1) return 'Just now';
    if (diffMinutes < 60) return `${diffMinutes} minutes ago`;
    if (diffMinutes < 1440) return `${Math.floor(diffMinutes / 60)} hours ago`;
    return date?.toLocaleDateString('en-GB');
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h2 className="text-xl font-semibold text-foreground">Integration Settings</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Connect and configure third-party accounting software and ERP systems
        </p>
      </div>
      {/* Connected Integrations */}
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-medium text-foreground mb-4 flex items-center">
            <Icon name="Link" size={20} className="mr-2 text-primary" />
            Connected Systems
          </h3>
          
          <div className="space-y-4">
            {integrations?.map((integration) => (
              <div key={integration?.id} className="bg-card border border-border rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-muted rounded-lg flex items-center justify-center">
                      <Icon name="Database" size={24} className="text-primary" />
                    </div>
                    <div>
                      <h4 className="text-lg font-medium text-foreground">{integration?.name}</h4>
                      <p className="text-sm text-muted-foreground">{integration?.type} System</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-4">
                    <div className="text-right">
                      <div className="flex items-center space-x-2">
                        {getStatusIcon(integration?.status)}
                        <span className={`text-sm font-medium capitalize ${getStatusColor(integration?.status)}`}>
                          {integration?.status}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        Last sync: {formatLastSync(integration?.lastSync)}
                      </p>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      {integration?.status === 'connected' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => syncNow(integration?.id)}
                          loading={isLoading}
                          iconName="RefreshCw"
                        >
                          Sync
                        </Button>
                      )}
                      
                      {integration?.status === 'connected' ? (
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => disconnectIntegration(integration?.id)}
                          iconName="Unlink"
                        >
                          Disconnect
                        </Button>
                      ) : (
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => connectIntegration(integration?.id)}
                          loading={isLoading}
                          iconName="Link"
                        >
                          Connect
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Configuration */}
                {integration?.status === 'connected' && (
                  <div className="border-t border-border pt-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {integration?.name === "SAP Business One" && (
                        <>
                          <Input
                            label="Server URL"
                            type="url"
                            value={integration?.config?.serverUrl}
                            onChange={(e) => handleIntegrationUpdate(integration?.id, 'serverUrl', e?.target?.value)}
                            size="sm"
                          />
                          <Input
                            label="Database"
                            type="text"
                            value={integration?.config?.database}
                            onChange={(e) => handleIntegrationUpdate(integration?.id, 'database', e?.target?.value)}
                            size="sm"
                          />
                          <Input
                            label="Username"
                            type="text"
                            value={integration?.config?.username}
                            onChange={(e) => handleIntegrationUpdate(integration?.id, 'username', e?.target?.value)}
                            size="sm"
                          />
                        </>
                      )}
                      
                      {integration?.name === "QuickBooks Online" && (
                        <>
                          <Input
                            label="Client ID"
                            type="text"
                            value={integration?.config?.clientId}
                            onChange={(e) => handleIntegrationUpdate(integration?.id, 'clientId', e?.target?.value)}
                            placeholder="Enter QuickBooks Client ID"
                            size="sm"
                          />
                          <Input
                            label="Redirect URI"
                            type="url"
                            value={integration?.config?.redirectUri}
                            onChange={(e) => handleIntegrationUpdate(integration?.id, 'redirectUri', e?.target?.value)}
                            size="sm"
                          />
                        </>
                      )}
                      
                      {integration?.name === "Microsoft Dynamics 365" && (
                        <>
                          <Input
                            label="Tenant ID"
                            type="text"
                            value={integration?.config?.tenantId}
                            onChange={(e) => handleIntegrationUpdate(integration?.id, 'tenantId', e?.target?.value)}
                            size="sm"
                          />
                          <Input
                            label="Client ID"
                            type="text"
                            value={integration?.config?.clientId}
                            onChange={(e) => handleIntegrationUpdate(integration?.id, 'clientId', e?.target?.value)}
                            size="sm"
                          />
                        </>
                      )}
                      
                      <Input
                        label="Sync Interval (minutes)"
                        type="number"
                        value={integration?.config?.syncInterval}
                        onChange={(e) => handleIntegrationUpdate(integration?.id, 'syncInterval', e?.target?.value)}
                        min="5"
                        max="1440"
                        size="sm"
                      />
                    </div>
                    
                    <div className="mt-4 flex items-center justify-between">
                      <Checkbox
                        label="Enable automatic synchronization"
                        checked={integration?.config?.autoSync}
                        onChange={(e) => handleIntegrationUpdate(integration?.id, 'autoSync', e?.target?.checked)}
                      />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Global Sync Settings */}
        <div>
          <h3 className="text-lg font-medium text-foreground mb-4 flex items-center">
            <Icon name="Settings" size={20} className="mr-2 text-primary" />
            Synchronization Settings
          </h3>
          
          <div className="bg-card border border-border rounded-xl p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center justify-between p-4 bg-muted rounded-xl">
                <div>
                  <h4 className="text-sm font-medium text-foreground">Global Auto-Sync</h4>
                  <p className="text-xs text-muted-foreground mt-1">
                    Enable automatic synchronization for all connected systems
                  </p>
                </div>
                <Checkbox
                  checked={syncSettings?.globalAutoSync}
                  onChange={(e) => handleSyncSettingChange('globalAutoSync', e?.target?.checked)}
                />
              </div>
              
              <Input
                label="Default Sync Frequency (minutes)"
                type="number"
                value={syncSettings?.syncFrequency}
                onChange={(e) => handleSyncSettingChange('syncFrequency', e?.target?.value)}
                min="5"
                max="1440"
                description="Default interval for new integrations"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Conflict Resolution Strategy
              </label>
              <select
                value={syncSettings?.conflictResolution}
                onChange={(e) => handleSyncSettingChange('conflictResolution', e?.target?.value)}
                className="w-full px-3 py-2 border border-border rounded-lg bg-input text-foreground focus:ring-2 focus:ring-ring focus:border-transparent"
              >
                <option value="manual">Manual Review Required</option>
                <option value="source">Source System Wins</option>
                <option value="destination">Audit AI Wins</option>
                <option value="timestamp">Latest Change Wins</option>
              </select>
            </div>
          </div>
        </div>

        {/* Data Mapping */}
        <div>
          <h3 className="text-lg font-medium text-foreground mb-4 flex items-center">
            <Icon name="ArrowLeftRight" size={20} className="mr-2 text-primary" />
            Data Mapping
          </h3>
          
          <div className="bg-card border border-border rounded-xl p-6">
            <p className="text-sm text-muted-foreground mb-4">
              Select which data types to synchronize between systems
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center justify-between p-4 bg-muted rounded-xl">
                <div>
                  <h4 className="text-sm font-medium text-foreground">Customer Data</h4>
                  <p className="text-xs text-muted-foreground mt-1">
                    Sync customer information and contact details
                  </p>
                </div>
                <Checkbox
                  checked={syncSettings?.dataMapping?.customers}
                  onChange={(e) => handleDataMappingChange('customers', e?.target?.checked)}
                />
              </div>
              
              <div className="flex items-center justify-between p-4 bg-muted rounded-xl">
                <div>
                  <h4 className="text-sm font-medium text-foreground">Product Catalog</h4>
                  <p className="text-xs text-muted-foreground mt-1">
                    Sync products, services, and pricing information
                  </p>
                </div>
                <Checkbox
                  checked={syncSettings?.dataMapping?.products}
                  onChange={(e) => handleDataMappingChange('products', e?.target?.checked)}
                />
              </div>
              
              <div className="flex items-center justify-between p-4 bg-muted rounded-xl">
                <div>
                  <h4 className="text-sm font-medium text-foreground">Invoices</h4>
                  <p className="text-xs text-muted-foreground mt-1">
                    Sync invoice data and transaction records
                  </p>
                </div>
                <Checkbox
                  checked={syncSettings?.dataMapping?.invoices}
                  onChange={(e) => handleDataMappingChange('invoices', e?.target?.checked)}
                />
              </div>
              
              <div className="flex items-center justify-between p-4 bg-muted rounded-xl">
                <div>
                  <h4 className="text-sm font-medium text-foreground">Payments</h4>
                  <p className="text-xs text-muted-foreground mt-1">
                    Sync payment records and transaction status
                  </p>
                </div>
                <Checkbox
                  checked={syncSettings?.dataMapping?.payments}
                  onChange={(e) => handleDataMappingChange('payments', e?.target?.checked)}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Actions */}
      <div className="flex items-center justify-between pt-6 border-t border-border">
        <div className="flex items-center space-x-2 text-sm text-muted-foreground">
          <Icon name="Shield" size={16} className="text-success" />
          <span>All integration data is encrypted and secure</span>
        </div>
        
        <div className="flex items-center space-x-3">
          <Button variant="outline" onClick={() => window.location?.reload()}>
            Reset Changes
          </Button>
          <Button 
            variant="default" 
            loading={isLoading}
            iconName="Save"
            iconPosition="left"
          >
            Save Integration Settings
          </Button>
        </div>
      </div>
    </div>
  );
};

export default IntegrationTab;

